import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import { RemoteStore } from '@/types/FoodDelivery.interface';

interface Props {
  integrationId: number | null;
  open: boolean;
  onClose: () => void;
  /** Seçilen mağaza kaydedildikten sonra çağrılır. */
  onSelected: (store: RemoteStore) => void;
}

/**
 * Sağlayıcıdaki mağazaları listeler ve seçileni entegrasyona yazar
 * (plan Faz 1.2) — kullanıcı mağaza kimliğini elle aramak zorunda kalmaz.
 */
const RemoteStorePickerDialog: React.FC<Props> = ({
  integrationId,
  open,
  onClose,
  onSelected
}) => {
  const [stores, setStores] = useState<RemoteStore[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !integrationId) return;
    setLoading(true);
    setError(null);
    foodDeliveryService
      .listRemoteStores(integrationId)
      .then(setStores)
      .catch((e) =>
        setError(
          (e as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? 'Mağaza listesi alınamadı.'
        )
      )
      .finally(() => setLoading(false));
  }, [open, integrationId]);

  const handlePick = async (store: RemoteStore) => {
    if (!integrationId) return;
    setSaving(store.id);
    setError(null);
    try {
      // Yalnız storeId gönderilir; diğer kimlik bilgileri sunucuda korunur.
      await foodDeliveryService.update(integrationId, {
        externalRestaurantId: store.id,
        credentials: { storeId: store.id }
      });
      onSelected(store);
      onClose();
    } catch (e) {
      setError(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Mağaza kaydedilemedi.'
      );
    } finally {
      setSaving(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Mağaza Seç</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Bu şubeye bağlanacak platform mağazasını seçin. Menü, fiyat ve
          uygunluk işlemleri seçtiğiniz mağaza üzerinden yapılır.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : stores.length === 0 && !error ? (
          <Alert severity="info">
            Bu hesapta mağaza bulunamadı. Platform panelinizden mağaza
            tanımlandığından emin olun.
          </Alert>
        ) : (
          <List>
            {stores.map((s) => (
              <ListItemButton
                key={s.id}
                disabled={saving != null}
                onClick={() => handlePick(s)}
              >
                <StorefrontIcon sx={{ mr: 2 }} color="action" />
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <span>{s.name}</span>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`ID: ${s.id}`}
                      />
                      {s.status && (
                        <Chip
                          size="small"
                          color={s.status === 'OPEN' ? 'success' : 'default'}
                          label={s.status === 'OPEN' ? 'Açık' : 'Kapalı'}
                        />
                      )}
                    </Stack>
                  }
                  secondary={s.address}
                />
                {saving === s.id && <CircularProgress size={18} />}
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Kapat</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RemoteStorePickerDialog;
